import fs from 'fs';
import path from 'path';
import chalk from 'chalk';
import type { ArgumentsCamelCase } from 'yargs';
import yargs from 'yargs';

import { CONFIG_PATH, DOCTYPE_PATH, RELTYPE_PATH } from './util/const';
import type { InitTree } from './util/tree';
import * as prompt from './util/prompt';

// node
import { add } from './cmds/add';
import { del } from './cmds/del';
import { move } from './cmds/move';
import { graft } from './cmds/graft';
import { prune } from './cmds/prune';
import { rename } from './cmds/rename';
import { rehead } from './cmds/rehead';
import { retypedoc, retyperef } from './cmds/retype';
import { check } from './cmds/check';
import { find } from './cmds/find';
import { REL_KINDS, status } from './cmds/status';
// garden
import { tree } from './cmds/tree';
import { list } from './cmds/list';
import { lint } from './cmds/lint';
import { doctor } from './cmds/doctor';
import { seed } from './cmds/seed';
// format
import { camlToYaml, mkdnToWiki, wikiToMkdn, yamlToCaml } from './cmds/convert';

// waiting on: https://github.com/tc39/proposal-import-assertions
// import pkg from '../package.json' assert { type: 'json' };

// helper to extract package.json values
export function getPkgObj() {
  const relPkgPath: string = '../package.json';
  const pkgPath: string = path.resolve(path.dirname(new URL(import.meta.url).pathname), relPkgPath);
  const fileContent: string = fs.readFileSync(pkgPath, 'utf-8').toString();
  return JSON.parse(fileContent);
}

// from: https://en.wikipedia.org/wiki/Command-line_interface#Command_description_syntax
// <> required
// [] optional
// ... list
// | or

export const tendr = (argv: string[], p: any = prompt): yargs.Argv => {
  const pkg: any = getPkgObj();
  return yargs(argv)
    .scriptName('tendr')
    .alias('tend', 't')
    .version(pkg.version)
    .usage('usage: $0 <command>\n\ncli tools for markdown-based digital gardening.')
    .demandCommand(1, 'please provide a valid command.')
    .help()
    .option('config', {
      alias: 'c',
      type: 'string',
      describe: 'relative path to config file, including filename; defaults to "./config.toml"',
      default: CONFIG_PATH,
    })
    .option('doctype', {
      alias: 'd',
      type: 'string',
      describe: 'relative path to doctype file, including filename; defaults to "t.doc.toml"',
      default: DOCTYPE_PATH,
    })
    .option('reltype', {
      type: 'string',
      describe: 'relative path to reltype file, including filename; defaults to "t.rel.toml"',
      default: RELTYPE_PATH,
    })
    // .wrap(null)
    // .epilogue('cli tools for markdown-based digital gardening')

    // garden level

    .command({
      command: 'doctor',
      aliases: ['doc', 'dr'],
      // aliases: [''],
      describe: 'check garden health',
      builder: (yargs: yargs.Argv) => yargs
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: any = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            reltypeUri: argv.reltype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined
          };
          doctor(payload);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'list',
      aliases: ['ls'],
      describe: 'list garden contents',
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined
          };
          list(payload);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'lint',
      // deprecated: `doctor` replaces `lint`
      describe: 'deprecated: use doctor',
      builder: (yargs: yargs.Argv) => yargs
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined
          };
          lint(payload);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'tree',
      // aliases: [''],
      describe: 'print full knowledge bonsai.',
      builder: (yargs: yargs.Argv) => yargs
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined
          };
          tree(payload, argv);
        } catch (e) {
          console.error(e);
        }
      },
    })

    // file level

    .command({
      command: 'check <concepts...>',
      describe: 'check which concepts exist, are referenced without files, or are missing entirely.',
      builder: (yargs: yargs.Argv) => yargs
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined,
          };
          const concepts: string[] = (argv.concepts as string[]) || [];
          check(concepts, payload, argv);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'status <filename>',
      aliases: ['stat'],
      describe: 'show status of file relationships.',
      builder: (yargs: yargs.Argv) => yargs
        .option('kind', {
          alias: 'k',
          type: 'string',
          describe: `kind of relationships to list\n(kinds: ${REL_KINDS.join(', ')}; default is "rel")`,
          default: 'rel',
        }),
      handler: async (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined,
          };
          return status(argv.filename as string, payload, argv);
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'find <fname>',
      aliases: ['f'],
      describe: 'show full path of markdown file(s) with the given filename.',
      builder: (yargs: yargs.Argv) => yargs
        .option('regex', {
          alias: 'r',
          type: 'boolean',
          describe: 'usage: find <regex>; use regex pattern instead of string -- this will find all filenames containing matches to the regex pattern. (use quotes around regex if the terminal is preemptively executing it)',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined,
          };
          return find(argv.fname as string, payload, argv);
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'add <fname>',
      aliases: ['a'],
      describe: 'create a new markdown file.',
      builder: (yargs: yargs.Argv) => yargs
        .option('type', {
          alias: ['t'],
          type: 'string',
          describe: 'document type name (section key in t.doc.toml), e.g. entry, book, index',
        })
        .option('path', {
          type: 'string',
          describe: 'override doctype directory path for this note',
        })
        .option('prefix', {
          type: 'string',
          describe: 'override doctype filename prefix for this note',
        })
        .option('attr', {
          alias: 'a',
          type: 'array',
          describe: 'set metadata attribute as key=value (repeatable), e.g. -a title="My Note" -a date=2026-01-01',
        })
        .option('body', {
          type: 'string',
          describe: 'markdown body after the front matter (or full file when there is no front matter)',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          add(argv.fname as string, argv);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'del <fname>',
      aliases: ['d'],
      describe: 'move a file to the trash.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt',
          default: false,
        }),
      handler: async (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm(`trash "${argv.fname}"`)) {
            await del(argv.fname as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'rm <fname>',
      describe: 'move a file to trash without confirmation.',
      handler: async (argv: ArgumentsCamelCase) => {
        try {
          await del(argv.fname as string, argv);
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'move <from> <to>',
      describe: 'move a markdown file on disk; syncs wikilinks when the basename changes.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('regex', {
          alias: 'r',
          type: 'boolean',
          describe: 'use regex replacement on paths; moves all files matching the pattern (use quotes around regex if the terminal is preemptively executing it)',
          default: false,
        })
        .option('title', {
          type: 'string',
          describe: 'explicit title value to set when the basename changes',
        })
        .option('noTitle', {
          alias: 'no-title',
          type: 'boolean',
          describe: 'skip updating the note title attribute when the basename changes',
          default: false,
        })
        .option('titleCase', {
          alias: ['title-case', 'case'],
          type: 'string',
          describe: 'override config.format.title_case (kinds: "Title Case", "lower case", "kabob-case", "snake_case")',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const run = () => move(argv.from as string, argv.to as string, argv);
          const relFrom: string = argv.from as string;
          const relTo: string = argv.to as string;
          if (argv.force || p.confirm(`move "${relFrom}" to "${relTo}"`)) {
            run();
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'rename <old-fname> <new-fname>',
      aliases: ['rn'],
      describe: 'rename a file and all of its references.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('regex', {
          alias: 'r',
          type: 'boolean',
          describe: 'usage: rename <regex> <replace-string>; use regex replacement instead of string replacement -- this will rename all filenames containing matches to the regex pattern; the regex argument will be replaced by the string argument. (use quotes around regex if the terminal is preemptively executing it)',
          default: false,
        })
        // title options
        .option('title', {
          type: 'string',
          describe: 'explicit title value to set on the renamed note',
        })
        .option('noTitle', {
          alias: 'no-title',
          type: 'boolean',
          describe: 'skip updating the renamed note title attribute',
          default: false,
        })
        .option('titleCase', {
          alias: ['title-case', 'case'],
          type: 'string',
          describe: 'override config.format.title_case (kinds: "Title Case", "lower case", "kabob-case", "snake_case")',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm(`rename "${argv.oldFname}" to "${argv.newFname}"`)) {
            rename(argv.oldFname as string, argv.newFname as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'rehead <old-fragment> <new-fragment>',
      aliases: ['rh'],
      describe: 'rewrite #header fragments on wikilinks and wikiembeds.',
      builder: (yargs: yargs.Argv) => yargs
        .option('fname', {
          alias: 'n',
          type: 'string',
          describe: 'target note filename (without .md extension)',
          demandOption: true,
        })
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const t: string = argv.fname as string;
          const o: string = argv.oldFragment as string;
          const n: string = argv.newFragment as string;
          if (argv.force || p.confirm(`rehead "${t}" links from "#${o}" to "#${n}"`)) {
            rehead(t, o, n, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'retypedoc <old-type> <new-type>',
      aliases: ['rtdoc', 'rtd'],
      describe: 'rename document type and update all occurrences.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm(`retype doctype "${argv.oldType}" to "${argv.newType}"`)) {
            retypedoc(argv.oldType as string, argv.newType as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'retyperef <old-type> <new-type>',
      aliases: ['rtref', 'rtr'],
      describe: 'rename reference type and all its occurrences.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('kind', {
          alias: 'k',
          type: 'string',
          describe: 'kind of entity to rename (kinds: "ref", "attr", "link"; default is "ref")',
          default: 'ref',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm(`retype ${argv.kind}type "${argv.oldType}" to "${argv.newType}"`)) {
            retyperef(argv.oldType as string, argv.newType as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'graft <child> <parent>',
      aliases: ['gr'],
      describe: 'reparent a note in the semantic tree index (wikilink list under a new parent).',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        })
        .option('dry-run', {
          type: 'boolean',
          describe: 'show what would change without writing files (skips confirmation)',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined,
          };
          const run = () => graft(argv.child as string, argv.parent as string, payload, argv);
          const dryRun: boolean = !!(argv as { dryRun?: boolean }).dryRun;
          if (argv.force || dryRun || p.confirm(`graft [[${argv.child}]] under [[${argv.parent}]]`)) {
            run();
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      },
    })

    .command({
      command: 'prune <node>',
      aliases: ['pr'],
      describe: 'remove a note from the semantic tree index (wikilink list entry).',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('single', {
          alias: 's',
          type: 'boolean',
          describe: 'remove only the bullet line; outdent nested children one level',
          default: false,
        })
        .option('node-only', {
          type: 'boolean',
          describe: 'same as --single',
          default: false,
        })
        .option('root', {
          alias: 'r',
          type: 'string',
          describe: 'filename for root of tree',
        })
        .option('glob', {
          alias: 'g',
          type: 'string',
          describe: 'glob to index files',
        })
        .option('dry-run', {
          type: 'boolean',
          describe: 'show what would change without writing files (skips confirmation)',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          const payload: InitTree = {
            configUri: argv.config as string,
            doctypeUri: argv.doctype as string,
            rootFileName: argv.root as string | undefined,
            globIndexUris: argv.glob as string | undefined,
          };
          const run = () => prune(argv.node as string, payload, argv);
          const dryRun: boolean = !!(argv as { dryRun?: boolean }).dryRun;
          if (argv.force || dryRun || p.confirm(`prune [[${argv.node}]] from the index`)) {
            run();
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      },
    })

  // convert

  // todo: (both links and aml)

  // .command({
  //   command: 'cmntowiki [glob]',
  //   aliases: ['ctow'],
  //   describe: 'convert from commonmark to wiki syntax.',
  //   handler: (argv: ArgumentsCamelCase) =>
  //     cmmnToWiki(argv.glob as string, argv),
  // })

  // .command({
  //   command: 'wikitocmn [glob]',
  //   aliases: ['wtoc'],
  //   describe: 'convert from wiki to commonmark syntax.',
  //   handler: (argv: ArgumentsCamelCase) =>
  //     wikiToCmmn(argv.glob as string, argv),
  // })

    .command({
      command: 'mkdntowiki [glob]',
      aliases: ['mtow'],
      describe: 'convert from "[markdown](style)" to "[[wiki-style]]" internal links.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('format', {
          alias: 'F',
          type: 'string',
          describe: 'how to parse markdown links -- "filename", "relative" urls, or "absolute" urls',
          default: 'filename',
        })
        .option('kind', {
          alias: 'k',
          type: 'string',
          describe: `kind of references to convert\n(kinds: ${REL_KINDS.join(', ')}; default is "rel")`,
          default: 'ref',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm('convert [markdown](links) to [[wikirefs]]')) {
            mkdnToWiki(argv.glob as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'wikitomkdn [glob]',
      aliases: ['wtom'],
      describe: 'convert from "[[wiki-style]]" to "[markdown](style)" internal links.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('format', {
          alias: 'F',
          type: 'string',
          describe: 'how to format the resulting markdown links -- "filename", "relative" urls, or "absolute" urls',
          default: 'filename',
        })
        .option('kind', {
          alias: 'k',
          type: 'string',
          describe: `kind of references to convert\n(kinds: ${REL_KINDS.join(', ')}; default is "rel")`,
          default: 'ref',
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm('convert [[wikirefs]] to [markdown](links)')) {
            wikiToMkdn(argv.glob as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'camltoyaml [glob]',
      aliases: ['ctoy'],
      describe: 'convert from "caml" to "yaml" style attributes.',
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm('convert attributes from caml to yaml')) {
            camlToYaml(argv.glob as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    .command({
      command: 'yamltocaml [glob]',
      aliases: ['ytoc'],
      describe: 'convert from "yaml" to "caml" style attributes.',
      // builder: (yargs: yargs.CommandBuilder<Record<string, any>, Record<string, any>>) => yargs
      builder: (yargs: yargs.Argv) => yargs
        .option('force', {
          alias: 'f',
          type: 'boolean',
          describe: 'skip verification prompt and perform operation',
          default: false,
        })
        .option('format', {
          alias: 'F',
          type: 'string',
          describe: 'how to format caml output (kinds: "none", "pretty", or "pad"; default is "none")',
          default: 'pretty',
        })
        .option('list-format', {
          alias: 'l',
          type: 'string',
          describe: 'how to format caml output lists (kinds: "mkdn" or "comma"; default is "mkdn")',
          default: 'mkdn',
        })
        .option('no-prefix', {
          alias: 'p',
          type: 'boolean',
          describe: 'do not use colon prefix in caml output',
          default: true,
        }),
      handler: (argv: ArgumentsCamelCase) => {
        try {
          if (argv.force || p.confirm('convert attributes from yaml to caml')) {
            yamlToCaml(argv.glob as string, argv);
          } else {
            p.abort();
          }
        } catch (e) {
          console.error(e);
        }
      }
    })

    // generative
    
    .command({
      command: 'seed <concept>',
      // aliases: [''],
      describe: 'seed a concept file from an llm.',
      builder: (yargs: yargs.Argv) => yargs
        // ai options
        .option('provider', {
          alias: 'p',
          type: 'string',
          describe: 'llm provider (anthropic, openai, xai)',
          default: 'anthropic',
        })
        .option('api-key', {
          alias: 'k',
          type: 'string',
          describe: 'api key (overrides stored key)',
        })
        .option('model', {
          alias: 'm',
          type: 'string',
          describe: 'model name (overrides default per provider)',
        })
        // output options
        .option('output', {
          alias: 'o',
          type: 'boolean',
          describe: 'write to <concept>.md file instead of stdout',
          default: false,
        })
        // formatting options
        .option('attrs', {
          type: 'string',
          describe: 'attribute format (caml, yaml)',
        })
        .option('case', {
          type: 'string',
          describe: 'case style (upper, lower)',
        })
        .option('text', {
          type: 'string',
          describe: 'text format (regular, [[wikitext]])',
        })
        .option('indent', {
          type: 'string',
          describe: 'indent style (2 spaces, 4 spaces, 1 tab)',
        })
        .option('whitespace', {
          type: 'string',
          describe: 'whitespace style (white space, snake_case, kabob-case)',
        }),
      handler: async (argv: ArgumentsCamelCase) => {
        try {
          await seed(argv.concept as string, argv);
        } catch (e: any) {
          console.error(chalk.red(e?.message || String(e)));
        }
      },
    });
    
};
